import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ProductInfo from "@/components/Product/ProductInfo";
import ProductEvaluation from "@/components/Product/ProductEvaluation";
import ReviewSection from "@/components/Product/ReviewSection";
import ProductDetail from "@/components/Product/ProductDetail";
import ContentSection from "@/components/Product/ContentSection";
import Divider from "@/components/common/Divider";
import api from "@/utils/api";
import { API_ENDPOINTS, ApiResponse } from "@/constants/api";
import { fontFamily } from "@/styles/font";
import logger from "@/utils/logger";

type TabType = "평가 정보" | "제품 정보" | "콘텐츠";

// API 응답 타입 정의
interface AlcoholData {
    altKey: string;
    name: string;
    type: string;
    abv: number;
    capacity: string | null;
    expirationPeriod: string | null;
    ingredients: string | null;
    breweryInfo: {
        breweryAltKey: string;
        breweryName: string;
        productionRegion: string;
    };
    price: string | null;
    awards: string[];
    salesUrlList: string[];
    thumbnailImageUrl: string | null;
    productFeatures: string | null;
    food: string | null;
    isBreweryProvidedAlcohol: boolean;
}

interface AlcoholApiResponse extends ApiResponse {
    data: AlcoholData;
}

interface ProductInfoProps {
    productAltKey: string;
    onMoveToReview: () => void;
    currentTab: string;
}

const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 430px;
    min-width: 320px;
    margin: 0 auto;
    min-height: 100vh;
    background: #ffffff;
    overflow-y: auto;
    margin-bottom: 91px;
`;

const MainContent = styled.div``;

const TabSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0;
    width: 100%;
    height: 44px;
    background: #ffffff;
    position: relative;
`;

const Tab = styled.div<{ $active?: boolean }>`
    /* settings-menu */
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 12px 0;
    width: 118.67px;
    height: 44px;
    background: #ffffff;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: ${(props) => (props.$active ? 700 : 400)};
    font-size: 16px;
    line-height: 19px;
    color: ${(props) => (props.$active ? "#007F73" : "#767676")};
    position: relative;
    cursor: pointer;
    flex: 1;

    &:not(:last-child)::after {
        content: "";
        position: absolute;
        right: 0;
        top: 12px;
        height: 20px;
        width: 1px;
        background-color: #e5e5e5;
    }
`;

const TabContent = styled.div`
    min-height: 200px;
    margin-top: 20px;
    position: relative;
    overflow-x: hidden;
    padding: 0 20px;

    h3 {
        color: #1f1f1f;
        margin-bottom: 16px;
    }
`;

const ProductInfoWrapper = styled.div`
    margin-top: 0;
    margin-bottom: 30px;
    padding: 0 20px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
    font-family: ${fontFamily.base};
    font-size: 16px;
    color: #767676;
`;

const ErrorWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    width: 100%;
    font-family: ${fontFamily.base};
    font-size: 16px;
    color: #ff0000;
`;

const Header = styled.header`
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    background-color: #ffffff;
    z-index: 100;
`;

const BackButton = styled.button`
    position: absolute;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;

    img {
        width: 24px;
        height: 24px;
    }
`;

const Title = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: #1f1f1f;
`;

const Product = () => {
    const { altKey } = useParams<{ altKey: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchTerm = searchParams.get("search");
    const [activeTab, setActiveTab] = useState<TabType>("제품 정보");
    const [alcoholData, setAlcoholData] = useState<AlcoholData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAlcoholData = async () => {
            try {
                setLoading(true);
                setError(null);

                // altKey가 없는 경우 API 호출 건너뛰기
                if (!altKey) {
                    setError("제품 ID가 없습니다.");
                    setLoading(false);
                    return;
                }

                // API 호출
                const response = await api.get<AlcoholApiResponse>(`${API_ENDPOINTS.ALCOHOLS}/${altKey}`);

                if (response.data.success) {
                    // API 응답의 속성 이름 매핑
                    const responseData = response.data.data;
                    setAlcoholData(responseData);
                } else {
                    setError(response.data.message || "데이터를 불러오는데 실패했습니다.");
                }
            } catch (err) {
                logger.error("제품 정보 조회 오류:", err);
                setError("제품 정보를 불러오는데 실패했습니다.");
            } finally {
                setLoading(false);
            }
        };

        if (altKey) {
            fetchAlcoholData();
        } else {
            setLoading(false);
            setError("제품 ID가 필요합니다.");
        }
    }, [altKey]);

    const handleBackButtonClick = () => {
        if (searchTerm) {
            // 검색어가 있는 경우 검색 페이지로 이동
            navigate(`/search?keyword=${encodeURIComponent(searchTerm)}`);
        } else {
            // 검색어가 없는 경우 일반 뒤로가기
            navigate(-1);
        }
    };

    // 리뷰 섹션으로 이동하는 함수 추가
    const handleMoveToReview = () => {
        setActiveTab("평가 정보");
        // 탭 전환 후 약간의 지연을 두고 스크롤
        setTimeout(() => {
            const reviewSection = document.getElementById("review-section");
            if (reviewSection) {
                reviewSection.scrollIntoView({ behavior: "smooth" });
            }
        }, 100);
    };

    const renderTabContent = () => {
        if (loading) {
            return <LoadingWrapper>로딩 중...</LoadingWrapper>;
        }

        if (error) {
            return <ErrorWrapper>{error}</ErrorWrapper>;
        }

        if (!alcoholData) {
            return <ErrorWrapper>제품 정보를 찾을 수 없습니다.</ErrorWrapper>;
        }

        switch (activeTab) {
            case "평가 정보":
                return (
                    <TabContent>
                        <ProductEvaluation productAltKey={altKey || ""} />
                        <Divider />
                        <ReviewSection productAltKey={altKey || ""} />
                    </TabContent>
                );
            case "제품 정보":
                return (
                    <TabContent>
                        <ProductDetail alcoholData={alcoholData} />
                    </TabContent>
                );
            case "콘텐츠":
                return (
                    <TabContent>
                        <ContentSection />
                    </TabContent>
                );
            default:
                return null;
        }
    };

    return (
        <Container>
            <Header>
                <BackButton onClick={handleBackButtonClick}>
                    <img src="/icon/chevron-left.png" alt="뒤로가기" />
                </BackButton>
            </Header>
            <MainContent>
                <ProductInfoWrapper>
                    {altKey && (
                        <ProductInfo
                            productAltKey={altKey}
                            onMoveToReview={handleMoveToReview}
                            currentTab={activeTab}
                        />
                    )}
                </ProductInfoWrapper>
                <TabSection>
                    <Tab $active={activeTab === "제품 정보"} onClick={() => setActiveTab("제품 정보")}>
                        제품 정보
                    </Tab>
                    <Tab $active={activeTab === "평가 정보"} onClick={() => setActiveTab("평가 정보")}>
                        평가 정보
                    </Tab>
                    <Tab $active={activeTab === "콘텐츠"} onClick={() => setActiveTab("콘텐츠")}>
                        콘텐츠
                    </Tab>
                </TabSection>
                {renderTabContent()}
            </MainContent>
        </Container>
    );
};

export default Product;
